import React, {useContext, useState} from "react";
import {Link} from "react-router-dom";
import {observer} from "mobx-react";
import {Button, Form, Input, Modal, Select, Space} from "antd";
import {DatabaseOutlined, LockOutlined, UserOutlined} from "@ant-design/icons";
import {FcGoogle} from "react-icons/fc";

import {paths} from "@/routes";
import stages, {StageName} from "@/shared/stages";
import StoreContext from "@/stores/StoreContext";

interface ISignInFormProps {
  onSubmit: (values: any) => Promise<any>;
  showStage: boolean;
}

export default observer(function SignInForm({showStage = false, onSubmit}: ISignInFormProps): JSX.Element {
  const store = useContext(StoreContext);
  const {stageStore} = store;
  const [submitting, setSubmitting] = useState(false);
  const [googleSubmitting, setGoogleSubmitting] = useState(false);
  const [form] = Form.useForm();

  const onFinish = async function onFinish(values: any) {
    setSubmitting(true);
    await onSubmit(values);
    setSubmitting(false);
  };

  const onSelectEnvChange = function onSelectEnvChange(stageName: StageName) {
    stageStore.setStage(stageName);
  };

  const signInWithGoogle = async function signInWithGoogle() {
    setGoogleSubmitting(true);
    try {
      await store.session.signInWithGoogle();
    } catch (error) {
      console.error(error);
      Modal.error({title: "Something went wrong", content: error?.reason || "Unknown error happened"});
    }
    setGoogleSubmitting(false);
  };

  return (
    <Form form={form} onFinish={onFinish} style={{width: 300}}>
      <Form.Item name="email" rules={[{required: true, message: "Please input your email"}]}>
        <Input
          prefix={<UserOutlined style={{color: "rgba(0,0,0,.25)"}} />}
          type="email"
          name="email"
          placeholder="Email"
          size="large"
        />
      </Form.Item>
      <Form.Item name="password" rules={[{required: true, message: "Please input your password"}]}>
        <Input
          prefix={<LockOutlined style={{color: "rgba(0,0,0,.25)"}} />}
          type="password"
          name="password"
          placeholder="Password"
          size="large"
        />
      </Form.Item>
      <Form.Item name="environment" initialValue={stageStore.stage.name} hidden={!showStage}>
        <Select placeholder="Environment" size="large" onChange={onSelectEnvChange}>
          {Object.values(stages).map(
            (env) =>
              // do not show development stage if we are not in development mode
              (process.env.NODE_ENV === "development" || env.name !== "development") && (
                <Select.Option key={env.name} value={env.name}>
                  <DatabaseOutlined style={{marginRight: 4}} />
                  {env.displayName}
                </Select.Option>
              ),
          )}
        </Select>
      </Form.Item>
      <Form.Item>
        <Space className="Buttons" direction="vertical" style={{width: "100%"}}>
          <Button type="primary" htmlType="submit" loading={submitting} disabled={googleSubmitting} size="large" block>
            Sign in
          </Button>
          <Button
            type="default"
            htmlType="button"
            loading={googleSubmitting}
            disabled={submitting}
            size="large"
            block
            onClick={signInWithGoogle}
          >
            <FcGoogle className="provider-icon" /> Sign in with Google
          </Button>
        </Space>
        <Link to={paths.passwordForgotten()}>
          <Button type="link" style={{width: "100%"}}>
            Forgot your password?
          </Button>
        </Link>
      </Form.Item>
    </Form>
  );
});
